import { z } from "zod";
import { BaseModelFields } from './base-model';

export interface ProductVariant extends BaseModelFields {
  storeId: number;
  productId: string;
  sku: string;
  name: string;
  productName: string;
  price: number; // Double value in dollars
  cost: number; // Double value in dollars
  quantity?: number | null;
  imageUrl?: string | null;
}

// Request/Response DTOs (single source of truth for API contracts)
export interface ProductVariantCreateRequest {
  storeId: number;
  productId: string;
  sku: string;
  name: string;
  price: number;
  cost: number;
  imageUrl?: string;
  color?: string | null;
  size?: string | null;
}

export interface ProductVariantUpdateRequest {
  storeId?: number;
  sku?: string;
  name?: string;
  price?: number;
  cost?: number;
  imageUrl?: string;
  color?: string | null;
  size?: string | null;
}

// Product variant form schema for create operations
export const productVariantCreateSchema = z.object({
  storeId: z.number().default(1),
  productId: z.string(),
  sku: z.string().min(1, "SKU is required"),
  name: z.string().min(1, "Variant name is required"),
  price: z.number().min(0, "Price must be positive"),
  cost: z.number().min(0, "Cost must be positive"),
  imageUrl: z.string().optional(),
  color: z.string().nullable().optional(),
  size: z.string().nullable().optional(),
});

// Product variant form schema for update operations
export const productVariantUpdateSchema = z.object({
  sku: z.string().min(1, "SKU is required").optional(),
  name: z.string().min(1, "Variant name is required").optional(),
  price: z.number().min(0, "Price must be positive").optional(),
  cost: z.number().min(0, "Cost must be positive").optional(),
  imageUrl: z.string().optional(),
  color: z.string().nullable().optional(),
  size: z.string().nullable().optional(),
});

// Product variant form values types
export type ProductVariantCreateFormValues = z.infer<typeof productVariantCreateSchema>;
export type ProductVariantUpdateFormValues = z.infer<typeof productVariantUpdateSchema>;

// Product variant search parameters
export type ProductVariantSearchParams = {
  productId: string;
  page?: number;
  size?: number;
  sort?: string;
};

// Product variant filters
export type ProductVariantFilters = {
  search?: string;
  productId?: string;
  hasStock?: boolean;
  minPrice?: number;
  maxPrice?: number;
};

// Product variant sort options
export type ProductVariantSortOption =
  | 'name,asc'
  | 'name,desc'
  | 'sku,asc'
  | 'sku,desc'
  | 'price,asc'
  | 'price,desc'
  | 'createdAt,asc'
  | 'createdAt,desc'
  | 'quantity,asc'
  | 'quantity,desc';

// Product variant bulk operations
export type ProductVariantBulkUpdate = {
  variantIds: string[];
  updates: Partial<ProductVariantUpdateFormValues>;
};

export type ProductVariantBulkDelete = {
  variantIds: string[];
  reason?: string;
};

// Utility types for forms and display
export type ProductVariantFormType = 'create' | 'update';
